import axios from 'axios';
import { getBlueprintUnderlayImagePath } from '@shared/helpers/design/blueprint-underlay-image-path.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';

/**
 * Decode a `data:` URL into a Blob for multipart upload.
 */
export const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const response = await fetch(dataUrl);
  return response.blob();
};

/**
 * Encode a fetched raster as a data URL for Mapbox / three.js.
 */
export const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (!guards.is.string(result) || !guards.is.nonEmptyString(result)) {
        reject(new Error('Failed to read blueprint image'));
        return;
      }
      resolve(result);
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(blob);
  });

/**
 * Upload a rasterized JPEG page to blob storage.
 * @returns Object key stored as {@link import('@shared/models/design/blueprint-underlay.model').BlueprintUnderlay.imageRef}
 */
export const uploadBlueprintUnderlayImage = async (
  projectId: number,
  underlayId: string,
  imageDataUrl: string,
): Promise<string> => {
  const blob = await dataUrlToBlob(imageDataUrl);
  const body = new FormData();
  body.append('file', blob, `${underlayId}.jpg`);
  const { data } = await axios.put<{ imageRef: string }>(
    getBlueprintUnderlayImagePath(projectId, underlayId),
    body,
  );
  return data.imageRef;
};

/**
 * Fetch a stored underlay raster.
 */
export const fetchBlueprintUnderlayImage = async (
  projectId: number,
  underlayId: string,
): Promise<Blob> => {
  const { data } = await axios.get<Blob>(
    getBlueprintUnderlayImagePath(projectId, underlayId),
    { responseType: 'blob' },
  );
  return data;
};
